// src/store/useProgressStore.js
import { create } from 'zustand';
import axiosClient from '../services/api';

const useProgressStore = create((set, get) => ({
  progress: null,
  allProgress: [],
  isLoading: false,
  error: null,

  fetchProgress: async (courseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/learning/progress/courses/${courseId}`);
      set({ progress: response.data.progress, isLoading: false });
      return response.data.progress;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch progress';
      set({ error: errorMessage, isLoading: false });
      return null;
    }
  },

  fetchAllProgress: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/learning/progress/courses');
      set({ allProgress: response.data.data, isLoading: false });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch progress';
      set({ error: errorMessage, isLoading: false });
      return null;
    }
  },

  markLessonComplete: async (lessonId, courseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post(`/learning/progress/lessons/${lessonId}/complete`, {
        courseId,
      });

      const currentProgress = get().progress;
      if (currentProgress) {
        const alreadyCompleted = currentProgress.completedLessons?.includes(lessonId);
        if (!alreadyCompleted) {
          set({
            progress: {
              ...currentProgress,
              completedLessons: [
                ...(currentProgress.completedLessons || []),
                lessonId,
              ],
            },
            isLoading: false,
          });
        } else {
          set({ isLoading: false });
        }
      } else {
        set({ isLoading: false });
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || 'Failed to mark lesson as complete';
      set({ error: errorMessage, isLoading: false });
      return null;
    }
  },

  updateQuizScore: async (quizId, score, percentage, passed, courseId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post(`/learning/progress/quizzes/${quizId}`, {
        score,
        percentage,
        passed,
        courseId
      });
      // Optimistically update local state
      const currentProgress = get().progress;
      if (currentProgress) {
        const updatedQuizScores = currentProgress.quizScores.map(q =>
          (q.quiz?._id || q.quiz) === quizId
            ? { ...q, score, percentage, passed, attempts: (q.attempts || 0) + 1 }
            : q
        );

        if (!currentProgress.quizScores.some(q => (q.quiz?._id || q.quiz) === quizId)) {
          updatedQuizScores.push({
            quiz: { _id: quizId },
            score,
            percentage,
            passed,
            attempts: 1
          });
        }

        set({
          progress: {
            ...currentProgress,
            quizScores: updatedQuizScores
          },
          isLoading: false
        });
      }
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update quiz score';
      set({ error: errorMessage, isLoading: false });
      return null;
    }
  }
}));

export default useProgressStore;